import { FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint } from "@progress/kendo-react-labels";
import * as React from "react";
import TagsInput from "react-tagsinput";
import FormLabel from "./FormLabel";
import { useControlledField, type FieldValidator } from "./useControlledField";

export interface FormTagsInputProps {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const FormTagsInput: React.FC<FormTagsInputProps> = ({
  name,
  validator,
  label,
  hint,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  disabled,
  placeholder,
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const inputId = name;
  const hintId = hint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";
  const value = (field.value as string[] | undefined) ?? [];

  const onInputChange = React.useCallback(
    (tags: string[]) => {
      field.onChange(tags);
    },
    [field],
  );

  return (
    <FieldWrapper>
      <FormLabel
        id={label ? `${inputId}_label` : ""}
        editorId={inputId}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={value.length > 0 ? value.join(",") : undefined}
        placeholder={placeholder}
        optional={optional}
      >
        <div
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            e.stopPropagation();
          }}
        >
          <TagsInput
            value={value}
            onChange={onInputChange}
            disabled={disabled}
            onlyUnique={true}
            addOnBlur={true}
            inputProps={{
              className: "react-tagsinput-input",
              placeholder: "",
              "aria-invalid": !!fieldState.error || undefined,
              "aria-describedby": `${hintId} ${errorId}`.trim() || undefined,
              onBlur: field.onBlur,
            }}
          />
        </div>
        {hint && <Hint id={hintId}>{hint}</Hint>}
        {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
      </FormLabel>
    </FieldWrapper>
  );
};

export default FormTagsInput;
